import { z } from 'zod';

/**
 * The "pre-qualify" subset of a Codeweavers motor-finance application that we
 * collect from a caller on a live call. Deliberately excludes sensitive fields
 * (bank sort code/account number, driving licence number, previous-address
 * history). All fields are optional — capture is best-effort.
 */
export const financeApplicationFieldsSchema = z.object({
  // Vehicle context
  vehicle_name: z.string().max(255).nullable().optional(),
  vehicle_vdp_url: z.string().nullable().optional(),
  finance_url: z.string().nullable().optional(),

  // Personal details
  title: z.string().max(20).nullable().optional(),
  forename: z.string().max(100).nullable().optional(),
  middle_name: z.string().max(100).nullable().optional(),
  surname: z.string().max(100).nullable().optional(),
  primary_telephone: z.string().max(50).nullable().optional(),
  secondary_telephone: z.string().max(50).nullable().optional(),
  email: z.string().max(255).nullable().optional(),
  date_of_birth: z.string().max(20).nullable().optional(),
  gender: z.string().max(20).nullable().optional(),
  nationality: z.string().max(100).nullable().optional(),
  marital_status: z.string().max(30).nullable().optional(),
  dependants: z.number().int().min(0).nullable().optional(),
  purchasing_for_business_use: z.boolean().nullable().optional(),

  // Current address
  postcode: z.string().max(20).nullable().optional(),
  house_name_number: z.string().max(100).nullable().optional(),
  address_line_1: z.string().max(255).nullable().optional(),
  address_line_2: z.string().max(255).nullable().optional(),
  address_line_3: z.string().max(255).nullable().optional(),
  town_city: z.string().max(100).nullable().optional(),
  county: z.string().max(100).nullable().optional(),
  years_at_address: z.number().int().min(0).nullable().optional(),
  months_at_address: z.number().int().min(0).max(11).nullable().optional(),
  accommodation_status: z.string().max(40).nullable().optional(),

  // Employment
  employment_basis: z.string().max(40).nullable().optional(),
  employment_category: z.string().max(60).nullable().optional(),
  employer: z.string().max(255).nullable().optional(),
  employer_phone: z.string().max(50).nullable().optional(),
  occupation: z.string().max(255).nullable().optional(),
  years_at_employment: z.number().int().min(0).nullable().optional(),
  months_at_employment: z.number().int().min(0).max(11).nullable().optional(),
  gross_annual_salary: z.number().int().min(0).nullable().optional(),

  notes: z.string().nullable().optional(),
});

export type FinanceApplicationFields = z.infer<typeof financeApplicationFieldsSchema>;

/** Create payload (used by the capture tool / programmatic create). */
export const createFinanceApplicationSchema = financeApplicationFieldsSchema.extend({
  call_id: z.string().uuid().nullable().optional(),
  contact_id: z.string().uuid().nullable().optional(),
  campaign_id: z.string().uuid().nullable().optional(),
  configuration_id: z.string().uuid().nullable().optional(),
  // True only on the FINAL save (everything gathered). Partial/interim saves
  // leave this unset so they persist as in-progress without firing the email.
  completed: z.boolean().optional(),
});

export type CreateFinanceApplicationDto = z.infer<typeof createFinanceApplicationSchema>;

/** Full row as returned by the API. */
export const financeApplicationSchema = financeApplicationFieldsSchema.extend({
  id: z.string().uuid(),
  call_id: z.string().uuid().nullable().optional(),
  contact_id: z.string().uuid().nullable().optional(),
  campaign_id: z.string().uuid().nullable().optional(),
  configuration_id: z.string().uuid().nullable().optional(),
  status: z.string(),
  applicant_name: z.string().nullable().optional(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type FinanceApplication = z.infer<typeof financeApplicationSchema>;

export const financeApplicationWithRelationsSchema = financeApplicationSchema.extend({
  call: z
    .object({
      id: z.string().uuid(),
      phone_number: z.string().nullable().optional(),
      contact: z
        .object({
          id: z.string().uuid(),
          name: z.string().nullable(),
          phone: z.string().nullable(),
        })
        .nullable()
        .optional(),
      campaign: z.object({ id: z.string().uuid(), name: z.string() }).nullable().optional(),
    })
    .nullable()
    .optional(),
});

export type FinanceApplicationWithRelations = z.infer<typeof financeApplicationWithRelationsSchema>;

export const financeApplicationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  campaignId: z.string().uuid().optional(),
  search: z.string().optional(),
  status: z.string().optional(),
  sortBy: z.enum(['created_at', 'applicant_name']).default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type FinanceApplicationQuery = z.infer<typeof financeApplicationQuerySchema>;
